import { AccountInfo } from "@azure/msal-browser";
import { ReactNode } from "react";

declare const window: Window & typeof globalThis & {
    inlineApexAdaptor: any,
    jsforce: any,
    baseUrl: string
}

export interface Record {
    sObjectTypeName: string;
    url: string;
    params?: any;
    headers?: any;
    method?: string;
    data?: any;
}

export interface Result {
    numberOfResults?: number;
    statusCode: number;
    result?: Record[];
    nextKey?: string;
    error?: string;
}

export interface VerifiedEntity extends Record {
    name?: string;
    sub?: string;
    firstName?: string;
    lastName?: string;
    id?: string;
    email?: string;
    key?: string;
    validUntilDateTime?: string;
    requestedDateTime?: string;
}

export interface Comment extends Record {
    Id: string;
    commentBody: string;
    createdDate: string;
    commenterFirstName: string;
    commenterLastName: string;
    commenterAlternativeName: string;
}

export interface Case extends Record {
    Id?: string;
    Subject?: string;
    Description?: string;
    IsClosed?: boolean;
}

export interface DynamicObjectType extends Record {
    Id?: string;
}
export interface DynamicObject extends Record {
    Id?: string;
}

export interface PortalConfig extends Record {
    Id?: string;
    debug?: Boolean;
    hideLayout?: Boolean;
}

export interface PortalUser extends Record {
    Id?: string;
}

export interface FieldSet {
    api_name: string;
    isRequired: boolean;
    isDependentPicklist: boolean;
    controllingPicklist: string;
    type: string;
    label: string;
    
}

export interface CaseModalState {
    Id: string;
    Subject: string;
    Description: string;
    Comment: string;
    IsClosed: boolean;
    modifiedFields: object;
    error: boolean;
    errorMessage: string | null;
    loading: boolean;
    deleteModal: boolean;
    hasModifiedFields: boolean;
    verifiedEntity: VerifiedEntity;
    items: Comment[];
    loadItems: boolean;
    loadingItems: boolean;
    item: Case | null;
    closeModal: boolean;
    saving: boolean;
    caseRow: Case | null;
    account: AccountInfo;
    Type: string;
    typeSelection: object | null;
    Sub_Type__c: string | null;
    subTypeSelection: object | null;
    fieldSet: FieldSet[] | null;
    loadFieldSet: boolean;
    loadingFieldSet: boolean;
    [key: string]: any;
}

export interface CloudPremisePluginRoute {
    route: string;
    component: ReactNode | null;
    privacy: string;
}

export interface CloudPremisePluginMenu {
    type?: string;
    name: string;
    key: string;
    icon?: ReactNode | string | null;
    route?: string;
    href?: string;
    noCollapse?: boolean;
    privacy?: string;
    [key: string]: any;
}

export class CloudPremisePluginOptions {
    name: string;
    description?: string;
    status: boolean;
    config?: any;
    routes?(props: any): CloudPremisePluginRoute[];
    menus?(props: any): CloudPremisePluginMenu[];
    userMenus?(props: any): CloudPremisePluginMenu[];
    footerMenus?(props: any): CloudPremisePluginMenu[];
    settingMenus?(props: any): CloudPremisePluginMenu[];
    render?: Function;
    onConfig?(plugin: CloudPremisePlugin);
    getProviders?(props: any): any;
    [key: string]: any;
}

export class CloudPremisePlugin extends CloudPremisePluginOptions {
    constructor(options: CloudPremisePluginOptions){
        super();
        for(let option in options){
            this[option] = options[option];
        }

        if(this.onConfig){
            this.onConfig = this.onConfig.bind(this);
        }
    }
    getProviders(){
        return [];
    }
    // onConfig(plugin: CloudPremisePlugin){
    //     console.log("OnConfig", this);
    // }
}

export interface CloudPremiseBackendPlugin {
    Configuration__c: string;
    Dependencies__c: string;
    DeveloperPluginName__c: string;
    Developer__c: string;
    HandlerType__c: string;
    Id: string;
    License__c: string;
    PluginType__c: string;
    PublicKey__c: string;
    QualifiedApiName: string;
    Status__c: string;
    Version__c: string;
    [key: string]: any;
}

export class CloudPremisePluginManager {
    plugins: CloudPremisePlugin[];
    configuredPlugins: CloudPremiseBackendPlugin[];
    constructor(plugins: CloudPremisePlugin[]){
        this.plugins = plugins;
        this.configuredPlugins = null;
    }
    render(): ReactNode {
        return this.getEnabledPlugins().map((plugin: CloudPremisePlugin) => {
            return plugin.render()
        })
    }
    getRoutes(props: any): CloudPremisePluginRoute[] {
        let routes = [];
        this.getEnabledPlugins().map((plugin: CloudPremisePlugin) => {
            routes = routes.concat(plugin.routes(props));
        });
        return routes;
    }
    getMenus(props: any): CloudPremisePluginMenu[] {
        let menus = [];
        this.getEnabledPlugins().map((plugin: CloudPremisePlugin) => {
            menus = menus.concat(plugin.menus(props));
        });
        return menus;
    }
    getUserMenus(props: any): CloudPremisePluginMenu[] {
        let menus = [];
        this.getEnabledPlugins().map((plugin: CloudPremisePlugin) => {
            menus = menus.concat(plugin.userMenus(props));
        });
        return menus;
    }
    getFooterMenus(props: any): CloudPremisePluginMenu[] {
        let menus = [];
        this.getEnabledPlugins().map((plugin: CloudPremisePlugin) => {
            menus = menus.concat(plugin.footerMenus(props));
        });
        return menus;
    }
    getSettingMenus(props: any): CloudPremisePluginMenu[] {
        let settingMenus = [];
        this.getEnabledPlugins().map((plugin: CloudPremisePlugin) => {
            settingMenus = settingMenus.concat(plugin.settingMenus(props));
        });
        return settingMenus;
    }
    getEnabledPlugins(){
        if(this.configuredPlugins === null){
            return this.plugins;
        }
        let enabledPlugins = {};
        this.getConfiguredPlugins().map((plugin: CloudPremiseBackendPlugin) => {
            if(plugin.Status__c === "Installed"){
                const pluginName = plugin.Developer__c+"/"+plugin.DeveloperPluginName__c;
                enabledPlugins[pluginName] = plugin;
            }
        });
        let plugins: CloudPremisePlugin[] = [];
        this.plugins.map((plugin: CloudPremisePlugin) => {
            if(enabledPlugins.hasOwnProperty(plugin.name)){
                plugins.push(plugin);
                try{
                    plugin.onConfig(enabledPlugins[plugin.name]);
                }catch(e){ }
            }
        });
        return plugins;
    }
    getConfiguredPlugins(){
        return this.configuredPlugins;
    }
    setConfiguredPlugins(configuredPlugins){
        this.configuredPlugins = configuredPlugins;
    }
    getProviders(){
        const plugins: CloudPremisePlugin[] = this.getEnabledPlugins();
        let providers = [];
        plugins.map((plugin) => {
            const pluginProviders = plugin.getProviders();
            if(Array.isArray(pluginProviders) && pluginProviders.length > 0){
                providers = providers.concat(pluginProviders);
            }
        });
        return providers;
    }
    getPluginByName(pluginName){
        let foundPlugin: CloudPremisePlugin = null;
        const plugins: CloudPremisePlugin[] = this.getEnabledPlugins();
        plugins.map((plugin) => {
            if(pluginName === plugin.name){
                foundPlugin = plugin;
            }
        });
        return foundPlugin;
    }
}

export { window };